export interface SessionClassData {
  classStartDate: string;
  classEndDate: string;
  schedule?: {
    [day: string]: {
      isActive: boolean;
    };
  };
}

// Convert time string to minutes for comparison
export const timeToMinutes = (timeStr: string): number => {
  const [time, period] = timeStr.trim().split(/\s+/);
  const [hours, minutes] = time.split(":").map(Number);
  let totalMinutes = (hours % 12) * 60 + (minutes || 0);
  if (period?.toUpperCase() === "PM" && hours !== 12) {
    totalMinutes += 12 * 60;
  } else if (period?.toUpperCase() === "AM" && hours === 12) {
    totalMinutes = minutes || 0;
  }
  return totalMinutes;
};

// Get next upcoming session time based on current time and class schedule
export const getNextUpcomingSessionTime = (
  classData: SessionClassData | null | undefined,
  sessionTime: string[] | null | undefined
): {
  time: string;
  index: number;
  date: Date;
} | null => {
  if (!classData || !sessionTime || sessionTime.length === 0) return null;

  const now = new Date();

  // Parse class dates
  let startDate = new Date(classData.classStartDate);
  if (isNaN(startDate.getTime())) {
    startDate = new Date();
  }

  // Start searching from whichever is later: now or startDate
  let searchDate = new Date(Math.max(now.getTime(), startDate.getTime()));
  searchDate.setHours(0, 0, 0, 0);

  const endDate = classData.classEndDate ? new Date(classData.classEndDate) : null;
  if (endDate) {
    endDate.setHours(23, 59, 59, 999);
    if (now > endDate) return null; // Class has ended
  }

  // Convert all session times to minutes and sort them
  const sessionTimesWithIndex = sessionTime
    .map((time, index) => ({
      time,
      index,
      minutes: timeToMinutes(time),
    }))
    .sort((a, b) => a.minutes - b.minutes);

  const daysOfWeek = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  // Search for the next available session up to 1 year ahead
  for (let i = 0; i < 365; i++) {
    const checkDate = new Date(searchDate);
    checkDate.setDate(searchDate.getDate() + i);

    if (endDate && checkDate > endDate) {
      return null;
    }

    const dayName = daysOfWeek[checkDate.getDay()];

    // Check if this day is active
    const isActiveDay = classData.schedule
      ? classData.schedule[dayName]?.isActive
      : true;

    if (isActiveDay) {
      if (checkDate.toDateString() === now.toDateString()) {
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const nextSessionToday = sessionTimesWithIndex.find(
          (s) => s.minutes > currentMinutes
        );

        if (nextSessionToday) {
          const sessionDate = new Date(checkDate);
          sessionDate.setHours(
            Math.floor(nextSessionToday.minutes / 60),
            nextSessionToday.minutes % 60,
            0,
            0
          );
          return {
            time: nextSessionToday.time,
            index: nextSessionToday.index,
            date: sessionDate,
          };
        }
      } else {
        const firstSession = sessionTimesWithIndex[0];
        const sessionDate = new Date(checkDate);
        sessionDate.setHours(
          Math.floor(firstSession.minutes / 60),
          firstSession.minutes % 60,
          0,
          0
        );
        return {
          time: firstSession.time,
          index: firstSession.index,
          date: sessionDate,
        };
      }
    }
  }

  return null;
};

// Check if any session is currently live
export const getCurrentLiveSession = (
  sessionTime: string[] | null | undefined
): { index: number; time: string } | null => {
  if (!sessionTime || sessionTime.length === 0) return null;

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const sessionDuration = 60; // Assume 1 hour session duration

  for (let i = 0; i < sessionTime.length; i++) {
    const sessionStart = timeToMinutes(sessionTime[i]);
    const sessionEnd = sessionStart + sessionDuration;

    if (currentMinutes >= sessionStart && currentMinutes <= sessionEnd) {
      return { index: i, time: sessionTime[i] };
    }
  }

  return null;
};

// Get time until next session
export const getTimeUntilNextSession = (
  classData: SessionClassData | null | undefined,
  sessionTime: string[] | null | undefined
): string => {
  const nextSession = getNextUpcomingSessionTime(classData, sessionTime);
  if (!nextSession || !nextSession.date) return "";

  const now = new Date();
  const diffMs = nextSession.date.getTime() - now.getTime();

  if (diffMs <= 0) return "";

  return `on ${nextSession.date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
};

// Get class status based on sessions
export const getClassStatus = (
  classData: SessionClassData | null | undefined,
  sessionTime: string[] | null | undefined
) => {
  const liveSession = getCurrentLiveSession(sessionTime);
  if (liveSession) {
    return {
      status: "live",
      message: `Session ${liveSession.index + 1} is live now`,
    };
  }

  const nextSession = getNextUpcomingSessionTime(classData, sessionTime);
  if (nextSession) {
    const timeUntil = getTimeUntilNextSession(classData, sessionTime);
    return { status: "upcoming", message: `Next session ${timeUntil}` };
  }

  return { status: "no-sessions", message: "No sessions scheduled" };
};

// Check if time string is a live session
export const isSessionLive = (
  timeStr: string,
  sessionTime: string[] | null | undefined
): boolean => {
  const liveSession = getCurrentLiveSession(sessionTime);
  return liveSession?.time === timeStr;
};

// Check if time string has passed for today
export const isSessionPassed = (timeStr: string): boolean => {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const sessionMinutes = timeToMinutes(timeStr);
  return sessionMinutes < currentMinutes;
};

// Check if the entire class is bookable (has live or upcoming sessions)
export const isClassBookable = (
  classData: SessionClassData | null | undefined,
  sessionTime: string[] | null | undefined
): boolean => {
  const status = getClassStatus(classData, sessionTime);
  return status.status !== "no-sessions";
};
